import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp, Overline } from "@/components/Reveal";
import { EditorialMarquee } from "@/components/Marquee";

const HERO_IMG =
  "https://images.unsplash.com/photo-1765498068971-01e6eaa856e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNhbG0lMjBuYXR1cmUlMjBzYW5kJTIwZHVuZXN8ZW58MHx8fHwxNzg2MDU4NDUxfDA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  {
    n: "01",
    title: "Subtract before you add",
    body: "Most businesses don't need another tool, hire, or hack. They need less. We find the 20% of your work creating 80% of the chaos — and delete it without mercy.",
  },
  {
    n: "02",
    title: "Systems that breathe",
    body: "Not rigid SOP binders nobody opens. Lightweight, living systems that run the boring parts of your business so your brain is free for the work only you can do.",
  },
  {
    n: "03",
    title: "Scale at your own pace",
    body: "Growth that respects your nervous system. Revenue up, hours down, and a calendar you're not afraid to open on Monday morning.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative pt-32 sm:pt-40 pb-20 sm:pb-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="text-xs uppercase tracking-[0.25em] font-medium text-neutral-500 mb-10"
            >
              For entrepreneurs drowning in their own success
            </motion.p>
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-[6.5rem] font-light tracking-tighter leading-[0.95] text-ink">
              <MaskedLine delay={0.35}>Cut the chaos.</MaskedLine>
              <MaskedLine delay={0.5}>Simplify the systems.</MaskedLine>
              <MaskedLine delay={0.65} className="italic text-sage">
                Scale what you love.
              </MaskedLine>
            </h1>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12 flex flex-col sm:flex-row sm:items-center gap-8"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="group w-fit rounded-full bg-sage text-sand px-8 py-4 text-sm tracking-wide hover:bg-ink transition-colors duration-300 flex items-center gap-3"
              >
                Start with a conversation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <p className="text-neutral-500 text-sm max-w-xs leading-relaxed">
                Without burnout. Without the 4am grind. Without becoming someone you don't recognize.
              </p>
            </motion.div>
          </div>

          <div className="lg:col-span-4 lg:-mb-28 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <motion.img
                src={HERO_IMG}
                alt="Rippled sand dunes — calm, ordered, expansive"
                data-testid="hero-image"
                style={{ y, scale }}
                className="w-full aspect-[3/4] object-cover will-change-transform"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section data-testid="manifesto-section" className="py-32 sm:py-44">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
      <FadeUp>
        <Overline className="mb-6">The KLA Manifesto</Overline>
        <h2 className="font-serif text-4xl sm:text-6xl font-light tracking-tight text-ink max-w-3xl text-balance">
          Three beliefs. Everything else is noise.
        </h2>
      </FadeUp>

      <div className="mt-24 sm:mt-32 flex flex-col gap-28 sm:gap-36">
        {CHAPTERS.map((c, i) => (
          <FadeUp key={c.n} delay={0.05 * i}>
            <div
              data-testid={`manifesto-chapter-${c.n}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-start ${
                i % 2 === 1 ? "md:pl-24" : ""
              }`}
            >
              <span className="md:col-span-3 font-serif text-7xl sm:text-8xl font-light text-clay/70 leading-none">
                {c.n}
              </span>
              <div className="md:col-span-7">
                <h3 className="font-serif text-3xl sm:text-4xl font-light text-ink mb-6">
                  {c.title}
                </h3>
                <p className="text-base sm:text-lg leading-relaxed text-neutral-600 max-w-xl">
                  {c.body}
                </p>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const ProgramsTeaser = () => (
  <section data-testid="programs-teaser-section" className="py-32 sm:py-40 bg-bone">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
      <FadeUp className="flex flex-col sm:flex-row sm:items-end justify-between gap-8 mb-20">
        <div>
          <Overline className="mb-6">Ways to work together</Overline>
          <h2 className="font-serif text-4xl sm:text-6xl font-light tracking-tight text-ink">
            Pick your pace.
          </h2>
        </div>
        <Link
          to="/programs"
          data-testid="programs-teaser-link"
          className="group flex items-center gap-2 text-sage text-sm tracking-wide hover:text-ink transition-colors duration-300"
        >
          Explore all programs
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
        </Link>
      </FadeUp>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <FadeUp className="lg:col-span-8" delay={0.05}>
          <Link
            to="/programs"
            data-testid="teaser-card-audit"
            className="group block bg-sand border border-border p-10 sm:p-14 h-full hover:border-sage transition-colors duration-500"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-8">One week · 1:1</p>
            <h3 className="font-serif text-3xl sm:text-5xl font-light text-ink mb-6 group-hover:text-sage transition-colors duration-300">
              The Chaos Audit
            </h3>
            <p className="text-neutral-600 leading-relaxed max-w-lg">
              A forensic week inside your business. We map every moving part, find the leaks, and hand you a one-page plan to reclaim 10+ hours a week.
            </p>
          </Link>
        </FadeUp>
        <FadeUp className="lg:col-span-4 lg:mt-16" delay={0.15}>
          <Link
            to="/programs"
            data-testid="teaser-card-sprint"
            className="group block bg-sage text-sand p-10 sm:p-14 h-full hover:bg-ink transition-colors duration-500"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-sand/60 mb-8">Six weeks · Cohort</p>
            <h3 className="font-serif text-3xl sm:text-4xl font-light mb-6">Systems Sprint</h3>
            <p className="text-sand/70 leading-relaxed">
              Build the five core systems every calm business runs on — alongside a small group of founders doing the same.
            </p>
          </Link>
        </FadeUp>
      </div>
    </div>
  </section>
);

const Quote = () => (
  <section data-testid="quote-section" className="py-32 sm:py-44">
    <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
      <FadeUp className="max-w-4xl">
        <p className="font-serif text-3xl sm:text-5xl font-light italic leading-snug text-ink text-balance">
          "I thought I needed to hire three people. Turns out I needed to delete half my business. Revenue is up 40% and I surf on Tuesdays now."
        </p>
        <p className="mt-10 text-sm tracking-wide text-neutral-500">
          — Maya R., founder of a 7-figure design studio
        </p>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      <Manifesto />
      <ProgramsTeaser />
      <Quote />
    </main>
  );
}
